/**
 * ImportTasksModal
 * -------------------------
 * A reusable modal component for importing tasks using a CSV/XLS file.
 *
 * FEATURES:
 * ✔ Drag & drop file import
 * ✔ Auto-detects file columns
 * ✔ Allows user to map uploaded columns → system fields (Owner, Status, Due Date, etc.)
 * ✔ Highlights duplicate field selections to prevent mapping conflicts
 * ✔ Allows enabling/disabling specific rows before importing
 * ✔ Shows full preview table of imported data
 * ✔ Sends formatted + filtered payload to backend via mapTasks()
 */

import Modal from "react-modal";
import { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import { Checkbox } from "@material-ui/core";
import { IoIosClose, IoMdDocument } from "react-icons/io";
import { handleFileUpload } from "../../../Services/contacts";
import { useDispatch } from "react-redux";
import Loading from "../../Loading/Loading";
import { mapTasks } from "../../../Services/resources";
import { nameList, ownerList } from "../../../Values/tables";

type props = {
  open: boolean;
  close: () => void;
  organization_id: string;
  usersList: any[];
  user: any;
};

const ImportTasksModal: FunctionComponent<props> = ({
  open,
  close,
  organization_id,
  usersList,
  user,
}) => {
  /** Extracted column headers from the uploaded file */
  const [columns, setColumns] = useState([]);

  /** Parsed row data from Excel/CSV file */
  const [data, setData] = useState([]);

  /** List of rows that user wants to EXCLUDE from import */
  const [select, setSelected] = useState<any[]>([]);

  /** Loading indicator while uploading or processing data */
  const [load, setLoad] = useState(false);

  /**
   * Column → System Field mapping
   * Example:
   * {
   *   "Uploaded Column A": "Owner",
   *   "Uploaded Column B": "Task Type",
   * }
   */
  const [colName, setColName] = useState<{ [key: string]: string }>({});

  /**
   * Available mapping choices.
   * `selected: true` means this field is already mapped → disable selection again.
   */
  const [choice, setChoice] = useState([
    { name: "Owner", selected: false },
    { name: "Customer Name", selected: false },
    { name: "Status", selected: false },
    { name: "Task Type", selected: false },
    { name: "Call Back Reason", selected: false },
    { name: "Due Date", selected: false },
    { name: "Completed Date", selected: false },
    { name: "Lead Id", selected: false },
    { name: "Created At", selected: false },
  ]);

  /**
   * Updates the 'selected' flag for each mapping choice.
   * Prevents mapping multiple columns to the same field.
   */
  useEffect(() => {
    const columns = Object.values(colName);
    let tempChoices = [...choice];
    tempChoices.forEach((choice, index) => {
      if (columns.includes(choice.name)) {
        tempChoices[index].selected = true;
      } else {
        tempChoices[index].selected = false;
      }
    });

    setChoice(tempChoices);
    // eslint-disable-next-line
  }, [colName]);

  /**
   * Auto-detect column types on upload using heuristics.
   * Example: If uploaded column is in `nameList` → default map to Customer Name.
   */
  useEffect(() => {
    let obj: any = {};

    columns.forEach((item: any) => {
      obj = {
        ...obj,
        [item.name]:
          nameList.includes(item.name) ? "Customer Name" :
          item.name === "lead id" ? "Lead Id" :
          item.name === "status" ? "Status" :
          ownerList.includes(item.name) ? "Owner" :
          item.name === "due date" ? "Due Date" :
          item.name === "completed date" ? "Completed Date" :
          item.name === "task type" ? "Task Type" :
          item.name === "call back reason" ? "Call Back Reason" :
          "",
      };
    });

    setColName(obj);
  }, [columns]);

  /**
   * Upload file handler
   * - Reads CSV/XLS
   * - Extracts row & column data
   * - Shows preview table
   */
  const upload = (file: any) => {
    handleFileUpload(
      file,
      (rows: any) => setData(rows),          // set table data
      (col: any) => setColumns(col),        // set column headers
      (loading: boolean) => setLoad(loading)
    );
  };

  const dispatcher = useDispatch();

  /**
   * Submit mapped and filtered task data to backend.
   */
  const onSubmit = () => {
    setLoad(true);

    /** Build final mapping payload */
    const mapping: { [key: string]: string } = {};

    Object.keys(colName).forEach((key) => {
      if (colName[key] !== "") mapping[colName[key]] = key;
    });

    /** Clone row list and remove excluded rows */
    let filteredRows = [...data];
    select.sort((a, b) => b - a); // remove from highest index first
    select.forEach((index) => filteredRows.splice(index, 1));

    /** Final API call */
    mapTasks(
      filteredRows,
      mapping,
      dispatcher,
      close,
      (loading: boolean) => setLoad(loading),
      organization_id,
      usersList,
      `${user.user_first_name} ${user.user_last_name}`
    );
  };

  /**
   * Handle user selecting a system field for an uploaded column.
   */
  const onSelect = (columnItem: any, selectedValue: string) => {
    let updated = { ...colName };

    Object.keys(updated).forEach((key) => {
      if (key === columnItem.name) {
        updated[key] = selectedValue === "Select" ? "" : selectedValue;
      }
    });

    setColName(updated);
  };

  return (
    <Modal
      style={{ overlay: { zIndex: 100 } }}
      className={styles.parent}
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      {load && <Loading />}

      {/* If data is loaded, show preview table */}
      {data.length !== 0 ? (
        <>
          {/* Close button */}
          <div className={styles.cross} onClick={close}>
            <IoIosClose size={35} color={"#808080"} />
          </div>

          <p className={styles.contactForm}>Import Data for "Tasks Form"</p>

          {/* Column mapping + row preview */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead className={styles.head}>
                <tr>
                  <th className={styles.th}></th>
                  <th className={styles.th}>Sno.</th>

                  {columns.map((col: any, index: number) => (
                    <th key={index} className={styles.th}>
                      {/* Uploaded column name */}
                      <div className={styles.tHeadContainer}>
                        <p>{col.name}</p>
                      </div>

                      {/* Mapping dropdown */}
                      <div className={styles.drop}>
                        <select
                          key={index}
                          className={styles.options}
                          onChange={(e) => onSelect(col, e.target.value)}
                        >
                          {/* Default value derived from detection */}
                          <option>
                            {nameList.includes(col.name)
                              ? "Customer Name"
                              : col.name === "lead id"
                              ? "Lead Id"
                              : col.name === "status"
                              ? "Status"
                              : ownerList.includes(col.name)
                              ? "Owner"
                              : col.name === "due date"
                              ? "Due Date"
                              : col.name === "completed date"
                              ? "Completed Date"
                              : col.name === "task type"
                              ? "Task Type"
                              : col.name === "call back reason"
                              ? "Call Back Reason"
                              : ""}
                          </option>

                          {/* Available mapping fields */}
                          {choice.map((choiceItem, i) => (
                            <option
                              disabled={choiceItem.selected}
                              key={i}
                            >
                              {choiceItem.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </th>
                  ))}

                  <th className={styles.th}></th>
                </tr>
              </thead>

              {/* TABLE BODY */}
              <tbody>
                {data.map((row: any, rowIndex: number) => (
                  <tr
                    key={rowIndex}
                    className={styles.no}
                    style={
                      select.includes(rowIndex)
                        ? { backgroundColor: "#00000030" }
                        : {
                            backgroundColor: "#ffffff",
                            borderBottomColor: "#f3f3f3",
                            borderBottomWidth: "5px",
                          }
                    }
                  >
                    <td>
                      <Checkbox
                        key={rowIndex}
                        checked={select.includes(rowIndex) ? false : true}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            let data = [...select];
                            data.push(rowIndex);
                            setSelected(data);
                          } else if (e.target.checked) {
                            let data = [...select];
                            let item = select.indexOf(rowIndex);
                            if (item > -1) {
                              data.splice(item, 1);
                            }
                            setSelected(data);
                          }
                        }}
                      />
                    </td>

                    <td className={styles.td}>{rowIndex + 1}</td>

                    {/* Render all row columns */}
                    {Object.values(row).map((val: any, i) => (
                      <td className={styles.td} key={i}>
                        {val}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ACTION BUTTONS */}
          <div className={styles.buttonBox}>
            <button className={styles.cancel} onClick={close}>
              Cancel
            </button>
            <button className={styles.confirm} onClick={onSubmit}>
              Confirm
            </button>
          </div>
        </>
      ) : (
        /* UPLOAD SCREEN BEFORE FILE IS LOADED */
        <div className={styles.child}>
          <div className={styles.cross} onClick={close}>
            <IoIosClose size={35} color={"#808080"} />
          </div>

          <p className={styles.contactForm}>Import Data for "Tasks Form"</p>

          {/* Drag to upload area */}
          <div
            className={styles.dragBox}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              upload(e.dataTransfer.files[0]);
            }}
          >
            <IoMdDocument size={100} color={"#808080"} />
            <p>Drag Your Files Here</p>
          </div>

          {/* File input */}
          <div className={styles.selectBox}>
            <label htmlFor="file-input" className={styles.uploadButton}>
              Upload File
            </label>
            <input
              id="file-input"
              className={styles.select}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e: any) => upload(e.target.files[0])}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ImportTasksModal;
